import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PersonId } from '@shared/brand';
import { useAccess } from './useAccess';

/**
 * Convierte el avatar de alguien en la puerta a su ficha — o lo deja como estaba.
 *
 * Existe para que las quince pantallas que pintan un avatar no repitan dos
 * decisiones que son la misma en todas: si quien mira tiene `ver_detalle`, y a
 * dónde se va. El sistema de diseño no puede tomarlas —no sabe de rutas ni de
 * permisos— y copiarlas pantalla por pantalla es la forma segura de que una se
 * quede atrás.
 *
 * Devuelve `null` cuando no hay a dónde ir, y ese `null` importa: un avatar que se
 * vuelve pulsable para todo el mundo y luego rebota a Inicio con «tu perfil no
 * tiene acceso» promete algo que no puede cumplir. Sin permiso, el avatar se queda
 * siendo lo que era.
 */
export const usePersonLink = (): ((
  personId: PersonId | string | null | undefined,
) => (() => void) | null) => {
  const { holds } = useAccess();
  const navigate = useNavigate();
  const allowed = holds('ver_detalle');

  return useCallback(
    (personId) => {
      if (!allowed) return null;
      if (personId === null || personId === undefined || personId === '') return null;

      return () => {
        void navigate(`/persona/${String(personId)}`);
      };
    },
    [allowed, navigate],
  );
};
